import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../utils/api';
import { setUnauthorizedHandler } from '../utils/api';
import API_CONFIG from '../config/api';
import { setAuthToken, removeAuthToken, initializeAuthToken } from '../utils/tokenManager';
import { nativeGoogleIdToken } from '../utils/socialAuth';
import { signupSource } from '../utils/attribution';

const AuthContext = createContext();

// Capacitor native shell (Android/iOS). On native we authenticate with a Bearer
// token because the webview origin is cross-site to the API (cookies won't ride);
// on web this is false and cookie auth is used unchanged.
const isNative = () => {
  try {
    return typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initEffectIdRef = useRef(0);

  // Register global 401 handler so expired sessions redirect to login
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      removeAuthToken();
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/auth/me');
      // React will automatically ignore state updates if component is unmounted
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching user info:', error);
      // If token is invalid, clear user state
      if (error.response?.status === 401) {
        setUser(null);
        removeAuthToken();
      }
      throw error;
    }
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    // Increment effect ID for this effect instance
    const effectId = ++initEffectIdRef.current;

    const initializeAuthState = async () => {
      try {
        // Native: load the persisted bearer token into memory FIRST, so the
        // fetchUserInfo request below carries the Authorization header. No-op on web.
        await initializeAuthToken();
        await fetchUserInfo();
      } catch (error) {
        // Silently handle 401 errors during initialization (user not logged in)
        // Other errors are already logged in fetchUserInfo
        if (error.response?.status !== 401) {
          console.error('Unexpected error during auth initialization:', error);
        }
      } finally {
        // Only update loading state if this is still the current effect instance
        if (effectId === initEffectIdRef.current) {
          setLoading(false);
        }
      }
    };

    initializeAuthState();
  }, [fetchUserInfo]);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/api/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear client-side state
      setUser(null);
      // Web: httpOnly cookie is cleared server-side. Native: drop the stored token.
      if (isNative()) {
        await removeAuthToken();
      }
    }
  }, []);

  const deleteAccount = useCallback(async ({ confirmation, password } = {}) => {
    // Permanently delete the account + all data. On success the server purges
    // everything and clears the web auth cookie; we drop client state (and the
    // native bearer token) exactly like logout so the app returns to the login
    // screen. Errors (wrong password, bad phrase) propagate to the caller.
    await apiClient.delete('/api/auth/me', { data: { confirmation, password } });
    setUser(null);
    if (isNative()) {
      await removeAuthToken();
    }
  }, []);

  const login = async (email, password) => {
    try {
      if (isNative()) {
        // Native: get the JWT in the body and store it for the Bearer header.
        const res = await apiClient.post('/api/auth/token', { email, password });
        await setAuthToken(res.data.access_token);
      } else {
        // Web: token is set as an httpOnly cookie by the server.
        await apiClient.post('/api/auth/login', { email, password });
      }

      // Fetch user info after successful login
      const userData = await fetchUserInfo();

      // Ensure user state is updated before returning
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed. Please check your email and password.'
      };
    }
  };

  const register = async (email, password) => {
    try {
      await apiClient.post('/api/auth/register', {
        email,
        password,
        source: signupSource(),
      });

      if (isNative()) {
        // Register sets an httpOnly cookie the native webview can't use cross-site,
        // so immediately exchange the same credentials for a Bearer token.
        const res = await apiClient.post('/api/auth/token', { email, password });
        await setAuthToken(res.data.access_token);
      }

      // Fetch user info after successful registration
      await fetchUserInfo();

      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const loginWithGoogle = async (credentialResponse) => {
    try {
      if (isNative()) {
        // Native: run Google's account picker (no webview navigation, so the
        // Capacitor bridge survives), send the ID token to the backend, and
        // store the returned bearer token — same path as email/password login.
        const idToken = await nativeGoogleIdToken();
        const res = await apiClient.post('/api/auth/google/native', { id_token: idToken });
        await setAuthToken(res.data.access_token);
        const userData = await fetchUserInfo();
        return { success: true, user: userData };
      }
      // Web: redirect to the backend Google OAuth endpoint (cookie session).
      window.location.href = `${API_CONFIG.BASE_URL}/api/auth/google`;
      return { success: true };
    } catch (error) {
      console.error('Google login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Google login failed',
      };
    }
  };

  const refreshUser = useCallback(async () => {
    await fetchUserInfo();
  }, [fetchUserInfo]);

  const isAuthenticated = useCallback(() => {
    return !!user;
  }, [user]);

  const value = {
    user,
    login,
    register,
    loginWithGoogle,
    logout,
    deleteAccount,
    refreshUser,
    isAuthenticated,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
